import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { Building2, Users, Search, X, TrendingUp, IndianRupee, RefreshCw, User } from "lucide-react";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

export default function RefCenterStats() {
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate]     = useState(today());
  const [createdById, setCreatedById] = useState("all");
  const [adminUsers, setAdminUsers]   = useState([]);
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    axiosInstance
      .get(`${API_BASE}/api/reports/filter-data`, { withCredentials: true })
      .then((r) => setAdminUsers(r.data?.adminUsers || []))
      .catch(() => {});
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axiosInstance.get(`${API_BASE}/api/reports/ref-center-stats`, {
        withCredentials: true,
        params: { fromDate, toDate, createdById },
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load statistics");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, createdById]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const setPreset = (preset) => {
    const now = new Date();
    if (preset === "today") {
      const d = today(); setFromDate(d); setToDate(d);
    } else if (preset === "week") {
      const s = new Date(now); s.setDate(now.getDate() - 6);
      setFromDate(s.toISOString().slice(0, 10)); setToDate(today());
    } else if (preset === "month") {
      setFromDate(monthStart()); setToDate(today());
    } else if (preset === "last-month") {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      setFromDate(s.toISOString().slice(0, 10));
      setToDate(e.toISOString().slice(0, 10));
    }
  };

  const inputCls =
    "px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500";

  const rows = (data?.rows || []).filter((r) =>
    !search.trim() ||
    r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.contactName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
          <Building2 size={18} className="text-indigo-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Reference Center Statistics</h1>
          <p className="text-xs text-gray-500">Orders referred by each reference center</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          {/* Quick presets */}
          <div>
            <p className="text-[10px] font-medium text-gray-500 mb-1.5">Quick Select</p>
            <div className="flex gap-1">
              {[["today","Today"],["week","7 Days"],["month","This Month"],["last-month","Last Month"]].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setPreset(k)}
                  className="px-2.5 py-1.5 text-[10px] font-medium rounded border border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div className="w-px h-8 bg-gray-200 hidden sm:block" />

          <div>
            <p className="text-[10px] font-medium text-gray-500 mb-1.5">From</p>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <p className="text-[10px] font-medium text-gray-500 mb-1.5">To</p>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={inputCls} />
          </div>

          {/* User filter */}
          <div>
            <p className="text-[10px] font-medium text-gray-500 mb-1.5">Created By</p>
            <select value={createdById} onChange={(e) => setCreatedById(e.target.value)} className={inputCls}>
              <option value="all">All Users</option>
              {adminUsers.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>

          <button
            onClick={fetchStats}
            className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition-colors"
          >
            <RefreshCw size={13} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 size={16} className="text-indigo-500" />
              <span className="text-xs font-medium text-gray-500">Active Ref Centers</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{data.summary?.totalCenters || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={16} className="text-blue-500" />
              <span className="text-xs font-medium text-gray-500">Total Orders</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{data.summary?.totalOrders || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <IndianRupee size={16} className="text-emerald-500" />
              <span className="text-xs font-medium text-gray-500">Total Amount</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{fmt(data.summary?.totalAmount)}</p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h2 className="text-sm font-semibold text-gray-800">Center-wise Breakdown</h2>
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search centers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-8 py-2 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-7 h-7 border-2 border-gray-200 border-t-indigo-600 rounded-full animate-spin" />
            <span className="text-xs text-gray-400">Loading statistics...</span>
          </div>
        ) : error ? (
          <div className="px-5 py-10 text-center text-xs text-red-500">{error}</div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-10 text-center text-xs text-gray-400">
            {search ? "No centers match your search" : "No orders with reference centers in this period"}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Center Name</th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-5 py-3 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Orders</th>
                  <th className="px-5 py-3 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Total Amount</th>
                  <th className="px-5 py-3 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Paid</th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Created By</th>
                  <th className="px-5 py-3 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Pending</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, i) => (
                  <tr key={row.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 py-3 text-gray-400">{i + 1}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-indigo-50 rounded-md flex items-center justify-center flex-shrink-0">
                          <Building2 size={13} className="text-indigo-500" />
                        </div>
                        <span className="font-medium text-gray-800">{row.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-gray-500">{row.contactName || "—"}</td>
                    <td className="px-5 py-3 text-right font-semibold text-gray-800">{row.totalOrders}</td>
                    <td className="px-5 py-3 text-right font-medium text-gray-800">{fmt(row.totalAmount)}</td>
                    <td className="px-5 py-3 text-right text-emerald-600 font-medium">{fmt(row.paidAmount)}</td>
                    <td className="px-5 py-3">
                      {(row.createdByUsers || []).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {row.createdByUsers.map((u, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">
                              <User size={9} />{u}
                            </span>
                          ))}
                        </div>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-5 py-3 text-right text-amber-600 font-medium">{fmt(row.pendingAmount)}</td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot className="bg-gray-900">
                  <tr>
                    <td className="px-5 py-3 text-white font-bold" colSpan={3}>Grand Total</td>
                    <td className="px-5 py-3 text-right text-white font-bold">
                      {rows.reduce((s, r) => s + r.totalOrders, 0)}
                    </td>
                    <td className="px-5 py-3 text-right text-white font-bold">
                      {fmt(rows.reduce((s, r) => s + r.totalAmount, 0))}
                    </td>
                    <td className="px-5 py-3 text-right text-emerald-400 font-bold">
                      {fmt(rows.reduce((s, r) => s + r.paidAmount, 0))}
                    </td>
                    <td className="px-5 py-3"></td>
                    <td className="px-5 py-3 text-right text-amber-400 font-bold">
                      {fmt(rows.reduce((s, r) => s + r.pendingAmount, 0))}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
